type MessageCaptionInput = {
  content?: string | null;
  metadata?: Record<string, unknown> | null;
};

function getMetaCaption(meta: Record<string, unknown>): string {
  const media = meta.media as { caption?: string } | undefined;
  const fromMeta = (meta.caption as string | undefined) || media?.caption || "";
  return normalizeCaptionText(fromMeta);
}

function extractWrapperCaption(content: string): string {
  const wrapperMatch = content.match(/^\[(?:Imagem|Vídeo|Video):\s*([\s\S]*)\]$/i);
  return wrapperMatch ? wrapperMatch[1].trim() : "";
}

/** Rótulos internos de mídia — não são legendas reais para exibir ao usuário. */
export function isMediaBodyPlaceholder(content: string | null | undefined): boolean {
  const text = (content || "").trim();
  if (!text) return true;
  if (extractWrapperCaption(text)) return false;
  return (
    text === "[Imagem]" ||
    text === "[imagem]" ||
    text.toLowerCase() === "imagem" ||
    text === "[Imagem enviada pelo cliente]" ||
    text === "[Imagem enviado]" ||
    /^\[Imagem.*enviad[oa]\]$/i.test(text) ||
    text === "[Vídeo]" ||
    text === "[Video]" ||
    text === "[video]" ||
    text === "[Vídeo enviado pelo cliente]" ||
    /^\[V[íi]deo.*enviad[oa]\]$/i.test(text) ||
    /\.(png|jpg|jpeg|webp|gif)$/i.test(text)
  );
}

function normalizeCaptionText(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const fromWrapper = extractWrapperCaption(trimmed);
  if (fromWrapper) return fromWrapper;
  if (isMediaBodyPlaceholder(trimmed)) return "";
  return trimmed;
}

export function isImagePlaceholderContent(
  content: string | null | undefined,
  metaType?: string,
): boolean {
  if (metaType !== "image") return false;
  return isMediaBodyPlaceholder(content);
}

export function isVideoPlaceholderContent(
  content: string | null | undefined,
  metaType?: string,
): boolean {
  if (metaType !== "video") return false;
  return isMediaBodyPlaceholder(content);
}

export function isSharePlaceholderContent(
  content: string | null | undefined,
  metaType?: string,
): boolean {
  if (metaType !== "share") return false;
  const text = (content || "").trim();
  return (
    !text ||
    text === "[Conteúdo compartilhado]" ||
    text.includes("lookaside.fbsbx.com") ||
    text.includes("scontent")
  );
}

const SHARE_LABEL_PATTERNS = [
  /^\[Conteúdo compartilhado\]$/i,
  /^\[Reel compartilhado\]$/i,
  /^\[Post compartilhado\]$/i,
  /^\[Convite recebido \(responda no app do Instagram\)\]$/i,
  /^\[Conteúdo não suportado\]$/i,
];

function isShareLabelOnly(content: string): boolean {
  return SHARE_LABEL_PATTERNS.some((pattern) => pattern.test(content.trim()));
}

export function resolveMediaCaption({
  content,
  metadata,
}: MessageCaptionInput): string | null {
  const meta = metadata ?? {};
  const metaType = meta.type as string | undefined;
  if (metaType !== "image" && metaType !== "video") return null;

  const captionFromMeta = getMetaCaption(meta);
  const captionFromBody = extractWrapperCaption(content || "");
  const isPlaceholder =
    metaType === "image"
      ? isImagePlaceholderContent(content, metaType)
      : isVideoPlaceholderContent(content, metaType);

  if (isPlaceholder) {
    const caption = captionFromMeta || captionFromBody;
    return caption || null;
  }

  const body = normalizeCaptionText(content || "");
  return captionFromMeta || body || null;
}

export function resolveShareCaption({
  content,
  metadata,
}: MessageCaptionInput): string | null {
  const meta = metadata ?? {};
  if (meta.type !== "share") return null;

  const captionFromMeta = getMetaCaption(meta);
  if (captionFromMeta) return captionFromMeta;

  const body = (content || "").trim();
  if (!body || isSharePlaceholderContent(body, "share") || isShareLabelOnly(body)) {
    return null;
  }

  return body;
}
